/**
 * Live previews for the catalog (2026-09-22): the element picker and the
 * sidebar draw every tile from the real markup, with the magazine's CSS and
 * the customer's CI, instead of a screenshot that nobody made. A customer's
 * own rows get a preview the same way, for free.
 *
 * One tile holds one FRAME per design of the type, so a hand moving across
 * the tile sweeps through the designs. A row without designs (a snippet, a
 * container, a plain element) has exactly one frame.
 *
 * Plain JS on purpose: the picker lives in the Vue 3 canvas bundle, the
 * sidebar in the Vue 2 host, and the host's webpack build resolves no .ts.
 */
import { getElementDesigns } from './elementSchemas';
import { designPreviewMarkup, stripEditorAttrs } from './designPreviewHtml';
import { isSwipe, normalizeSettingsV3 } from './v2ContainerSchemas';
import { effectiveValue } from './authoredTiers';
import { ensureWmId } from './wmId';
import { collectPageV2Css } from '../../utils/buildPageV2Css';
import { upgradeFormat1 } from '../model/section/upgrade';

/** The width the frames are laid out at before they are shrunk into a tile. */
export const PREVIEW_FRAME_WIDTH = 760;
/**
 * A container is laid out wider: its columns stand side by side from the
 * md breakpoint (768px) only, and a tile should show the desktop shape of
 * a layout, not its stacked phone shape.
 */
export const PREVIEW_CONTAINER_WIDTH = 1200;

// v2 markup, or a Bereich of the Mischweg (rendered from data, no data-wps-*).
const V2_ROOT_RE = /^\s*<section\b[^>]*(\bdata-wps-version="2"|\bclass="wm-section[\s"])/;

/** Only v2 markup is drawn live; the classic snippets keep their pictures. */
export function isV2Markup(html) {
  return V2_ROOT_RE.test(String(html || ''));
}

function elementTypeOf(html) {
  const hit = /^\s*<section\b[^>]*\bdata-wps-name="([^"]+)"/.exec(html);
  return hit ? hit[1] : '';
}

const COL_RE = /<div\b[^>]*\bdata-v2-col="\d+"[^>]*>/g;
const EMPTY_COL_RE = /<div\b[^>]*\bdata-v2-col="\d+"[^>]*>\s*<\/div>/g;

/**
 * A container template has nothing in its columns, so a live frame of it is
 * a white square. Its tile is a SKETCH instead, drawn from the settings:
 * one box per column, as wide as the column is on a desktop, marked when
 * the column carries a picture, with arrows when the row is swiped.
 * `null` for anything that is not an empty v2 container.
 */
export function containerSketch(html) {
  const text = String(html || '');
  if (!isV2Markup(text) || !/^\s*<section\b[^>]*\bdata-wps-v2-container="true"/.test(text)) return null;
  const cols = (text.match(COL_RE) || []).length;
  if (!cols || (text.match(EMPTY_COL_RE) || []).length !== cols) return null;
  const raw = /\bdata-wps-settings='([^']*)'/.exec(text);
  let settings;
  try {
    settings = normalizeSettingsV3(raw ? JSON.parse(raw[1]) : {});
  } catch {
    return null;
  }
  const columns = (settings.columns || []).slice(0, cols).map((col) => ({
    span: Number(effectiveValue(col.span, '2xl', 12)) || 12,
    image: ['image', 'video', 'youtube'].includes(col.background && col.background.type),
  }));
  while (columns.length < cols) columns.push({ span: 12, image: false });
  return { columns, swipe: isSwipe(settings) };
}

/**
 * The sketch of an empty Bereich (a layout row, feedback 24.09.2026): mode
 * "frei" and no element in any cell. One box per column, as wide as the
 * column is on a desktop (all equal); arrows when it swipes. `null` otherwise.
 */
export function bereichSketch(stored) {
  // a catalog row saved before the English rename holds a format 1 doc
  const doc = upgradeFormat1(stored);
  const b = doc && doc.section;
  if (!b || b.mode !== 'free' || !Array.isArray(doc.cells) || !doc.cells.length) return null;
  if (doc.cells.some((z) => (z.structure || []).length > 0)) return null;
  const columns = (b.layout && b.layout.columns && b.layout.columns.all) || doc.cells.length;
  const span = Math.max(1, Math.round(12 / columns));
  return {
    columns: doc.cells.map(() => ({ span, image: false })),
    swipe: !!(b.layout && b.layout.kind && b.layout.kind.all === 'swipe'),
  };
}

/**
 * The frames of one tile: `{ key, label: { de, en }, markup }`, one per
 * design, or a single one when the type has none. Empty for markup that is
 * not v2.
 */
export function previewFrames(html) {
  if (!isV2Markup(html)) return [];
  const clean = stripEditorAttrs(html);
  const type = elementTypeOf(clean);
  const designs = getElementDesigns(type);
  if (!designs.length) return [{ key: 'plain', label: { de: '', en: '' }, markup: clean }];
  return designs.map((d) => ({
    key: d.key,
    label: d.label,
    markup: designPreviewMarkup(clean, d.recipe, type),
  }));
}

/**
 * The hand's place on a tile picks the frame: left edge = first design,
 * right edge = last. Outside the tile, the first one.
 */
export function frameIndexAt(x, width, count) {
  if (count < 2 || width <= 0) return 0;
  const ratio = Math.min(Math.max(x / width, 0), 0.999);
  return Math.floor(ratio * count);
}

function escapeHtml(text) {
  return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
}

/**
 * A whole document for a tile that lives OUTSIDE the canvas (the sidebar in
 * the Vue 2 host): the magazine's stylesheets, the CI tokens, every frame
 * stacked, and a tiny script that switches frames on `wm-preview-frame`
 * messages from the host. The frame is laid out at PREVIEW_FRAME_WIDTH and
 * shrunk with `zoom`, the same trick the design picker uses: zoom shrinks the
 * LAYOUT too, so the tile shows the element as the page would.
 */
/**
 * A row's layout CSS (column widths, backgrounds, heights) is not in the
 * markup: the editor and the publisher emit it per node id. A catalog row
 * has no ids yet, so the frame gives every node one and emits the CSS
 * itself -- or the columns of a snippet would stack in the tile.
 */
function withLayoutCss(markup) {
  let doc;
  try {
    doc = new DOMParser().parseFromString(markup, 'text/html');
  } catch {
    // No DOM here (a plain node test): the markup goes in as it is.
    return { markup, css: '' };
  }
  doc.body.querySelectorAll('[data-wps-settings]').forEach((el) => ensureWmId(el));
  const html = doc.body.innerHTML;
  return { markup: html, css: collectPageV2Css(html) };
}

/** The width a set of frames is laid out at: wide for a container, see above. */
export function previewFrameWidth(frames) {
  // Containers and Bereiche (their tablet/phone tiers start below 1024px/768px) show their desktop shape.
  return frames.some((f) => /^\s*<section\b[^>]*(\bdata-wps-v2-container="true"|\bclass="wm-section[\s"])/.test(f.markup))
    ? PREVIEW_CONTAINER_WIDTH : PREVIEW_FRAME_WIDTH;
}

/**
 * The document of a tile's frame. It is laid out at its full width and NOT
 * shrunk in here: media queries read the viewport, so a frame that is 300px
 * wide would always show the phone shape, whatever CSS zoom does to the
 * layout inside. The tile makes the frame wide and scales it down instead;
 * the frame tells the tile how tall it came out.
 */
export function previewFrameDocument(frames, { cssUrls = [], tokenCss = '', locale = 'de' } = {}) {
  if (!frames.length || !cssUrls.length) return '';
  const links = cssUrls.map((url) => `<link rel="stylesheet" href="${escapeHtml(url)}">`).join('');
  const lang = String(locale).startsWith('en') ? 'en' : 'de';
  const styled = frames.map((f) => withLayoutCss(f.markup));
  const layoutCss = styled.map((f) => f.css).join('\n');
  const width = previewFrameWidth(frames);
  const body = frames.map((f, i) => `<div class="f${i === 0 ? ' is-on' : ''}" data-label="${escapeHtml(f.label[lang] || '')}">${styled[i].markup}</div>`).join('');
  const dots = frames.length > 1
    ? `<div class="d">${frames.map((f, i) => `<i${i === 0 ? ' class="is-on"' : ''}></i>`).join('')}</div><div class="l"></div>`
    : '';
  return `<!doctype html><html><head><meta charset="utf-8">${links}<style>${tokenCss}</style><style>${layoutCss}</style><style>
    html, body { margin: 0; background: #fff; overflow: hidden; width: ${width}px; }
    .f { display: none; width: ${width}px; pointer-events: none; }
    .f.is-on { display: block; }
    .d { position: fixed; left: 0; right: 0; bottom: 4px; display: flex; justify-content: center; gap: 3px; }
    .d i { width: 5px; height: 5px; border-radius: 50%; background: rgba(0,0,0,.25); }
    .d i.is-on { background: #4f46e5; }
    .l { position: fixed; left: 6px; bottom: 12px; font: 11px system-ui, sans-serif; color: #1f2430; background: rgba(255,255,255,.85); padding: 1px 6px; border-radius: 4px; }
    .l:empty { display: none; }
    .fr-selection-handle, .fr-quick-insert, .fr-toolbar { display: none !important; }
    /* No reader runtime runs in a tile: marks that wait for their grow-in (chart animation) must show (feedback 29.09.2026). */
    .wm-chart-surface [ecmeta_ssr_type="chart"] { opacity: 1 !important; }
  </style></head><body>${body}${dots}
  <script>
    var frames = document.querySelectorAll('.f'), dots = document.querySelectorAll('.d i'), label = document.querySelector('.l');
    // The tile takes the height of what is shown, so the whole row is
    // visible, not just its top edge.
    function report() {
      window.parent.postMessage({ type: 'wm-preview-size', height: document.documentElement.scrollHeight }, '*');
    }
    function show(n) {
      for (var i = 0; i < frames.length; i += 1) {
        frames[i].classList.toggle('is-on', i === n);
        if (dots[i]) dots[i].classList.toggle('is-on', i === n);
      }
      if (label) label.textContent = n > 0 && frames[n] ? frames[n].getAttribute('data-label') : '';
      report();
    }
    window.addEventListener('message', function (e) {
      if (e.source !== parent) return;
      if (e.data && e.data.type === 'wm-preview-frame') show(e.data.index || 0);
    });
    window.addEventListener('load', report);
    report();
  </script></body></html>`;
}
